type WebSocketType = "chat" | "events";

interface WebSocketConfig {
  url: string;
  onMessage?: (event: MessageEvent) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onOpen?: () => void;
}

class WebSocketConnection {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers = new Set<(event: MessageEvent) => void>();

  constructor(config: WebSocketConfig) {
    this.config = config;
  }

  updateHandlers(handlers: Partial<WebSocketConfig>) {
    if (handlers.onError) {
      this.config.onError = handlers.onError;
    }
    if (handlers.onClose) {
      this.config.onClose = handlers.onClose;
    }
    if (handlers.onOpen) {
      this.config.onOpen = handlers.onOpen;
    }
    // Note: We don't update onMessage since we use addMessageHandler pattern
  }

  connect() {
    // Check for both OPEN and CONNECTING states to avoid duplicate connections
    if (
      this.ws?.readyState === WebSocket.OPEN ||
      this.ws?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    this.ws = new WebSocket(this.config.url);

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.config.onOpen?.();
    };

    this.ws.onmessage = (event) => {
      for (const handler of this.messageHandlers) {
        try {
          handler(event);
        } catch (error) {
          console.error("Error in message handler:", error);
        }
      }
      this.config.onMessage?.(event);
    };

    this.ws.onerror = (error) => {
      this.config.onError?.(error);
      this.handleReconnect();
    };

    this.ws.onclose = () => {
      this.config.onClose?.();
      this.handleReconnect();
    };
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempts - 1), 10000);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  send(data: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }

  addMessageHandler(handler: (event: MessageEvent) => void) {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  get isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

class WebSocketManager {
  private static instance: WebSocketManager;
  private connections: Map<string, WebSocketConnection> = new Map();

  private constructor() {}

  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  getConnection(
    type: WebSocketType,
    config: WebSocketConfig,
  ): WebSocketConnection {
    const key = type;
    let connection = this.connections.get(key);

    if (!connection) {
      const url = type === "events" ? "/api/events/ws" : config.url;
      connection = new WebSocketConnection({ ...config, url });
      this.connections.set(key, connection);
    } else {
      // Update the config handlers for existing connection
      // This handles React Strict Mode's double mounting
      connection.updateHandlers(config);
    }

    return connection;
  }

  disconnectAll() {
    for (const connection of this.connections.values()) {
      connection.disconnect();
    }
    this.connections.clear();
  }

  removeConnection(type: WebSocketType) {
    const connection = this.connections.get(type);
    if (connection) {
      connection.disconnect();
      this.connections.delete(type);
    }
  }
}

export const wsManager = WebSocketManager.getInstance();
